/**
 * fetchApi — thin wrapper around the project's axios instance.
 *
 * All requests go through utils/axios.js which:
 *  - sets baseURL from VITE_REACT_APP_API_BASE_URL
 *  - injects a fresh Keycloak Bearer token on every request (request interceptor)
 *  - retries once on 401 after a token refresh (response interceptor)
 *
 * The public API (.get, .post, .put, .patch, .delete, .postBlob, .getBlob) is kept
 * identical to the old fetch-based version so no call sites need to change.
 */
import axiosInstance from 'utils/axios';

export default function fetchApi(endpoint, options = {}) {
  // Strip a leading slash — axios baseURL already ends without one.
  const path = `api/${endpoint.replace(/^\//, '')}`;

  // Merge any extra headers the caller wants to pass (rarely needed since
  // the interceptor handles Authorization automatically).
  const extraHeaders = options.headers ?? {};

  const handleResponse = (response) => response.data;

  const handleError = (error) => {
    // axios interceptor re-throws error.response.data for non-401 errors.
    // Re-wrap so callers get the same shape as before.
    const data = error?.response?.data ?? error;
    const err = new Error(data?.message || error?.message || 'Request failed');
    err.status = error?.response?.status ?? error?.status;
    err.response = data;
    throw err;
  };

  return {
    get: (params = '') =>
      axiosInstance
        .get(`${path}${params}`, { headers: extraHeaders })
        .then(handleResponse)
        .catch(handleError),

    post: (body) => {
      const isFormData = body instanceof FormData;
      return axiosInstance
        .post(path, body, {
          headers: {
            ...(!isFormData ? { 'Content-Type': 'application/json' } : {}),
            ...extraHeaders,
          },
        })
        .then(handleResponse)
        .catch(handleError);
    },

    postBlob: (body) =>
      axiosInstance
        .post(path, body, {
          responseType: 'blob',
          headers: { 'Content-Type': 'application/json', ...extraHeaders },
        })
        .then((res) => res.data)
        .catch(handleError),

    getBlob: (params = '') =>
      axiosInstance
        .get(`${path}${params}`, {
          responseType: 'blob',
          headers: extraHeaders,
        })
        .then((res) => res.data)
        .catch(handleError),

    patch: (body) =>
      axiosInstance
        .patch(path, body, {
          headers: { 'Content-Type': 'application/json', ...extraHeaders },
        })
        .then(handleResponse)
        .catch(handleError),

    put: (body) =>
      axiosInstance
        .put(path, body, {
          headers: { 'Content-Type': 'application/json', ...extraHeaders },
        })
        .then(handleResponse)
        .catch(handleError),

    delete: (params) => {
      const hasBody =
        params &&
        typeof params === 'object' &&
        !(params instanceof URLSearchParams);

      return axiosInstance
        .delete(hasBody ? path : `${path}${params || ''}`, {
          headers: { 'Content-Type': 'application/json', ...extraHeaders },
          ...(hasBody ? { data: params } : {}),
        })
        .then(handleResponse)
        .catch(handleError);
    },
  };
}

